import React, { useState, useContext, useRef } from "react";
import { makeStyles, useTheme, alpha } from "@material-ui/core/styles";
import { blue, green } from "@material-ui/core/colors";
import { isDarkTheme } from "../../helpers/darkTint";
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  LinearProgress,
  Box,
  Badge,
} from "@material-ui/core";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Event as EventIcon,
  Flag as FlagIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  WhatsApp as WhatsAppIcon,
  Schedule as ScheduleIcon,
  Send as SendIcon,
  Link as LinkIcon,
} from "@material-ui/icons";
import { format } from "date-fns";
import TasksModal from "./TasksModal";
import TicketModal from "../TicketModal";
import NewTicketModal from "../NewTicketModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SettingsContext } from "../../context/Settings/SettingsContext";
import {
  isPerConnectionContactTickets,
  ticketsOnOwnConnections,
} from "../../helpers/contactTicketMode";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { useKanbanCardHeight } from "./cardHeight";

// A altura do card não é fixa: todos os cards do quadro recebem a altura do
// maior card visível nele (ver cardHeight.js), para as colunas ficarem
// uniformes sem reservar espaço que nenhum card usa.
const META_LINE_HEIGHT = 19;

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(0.75),
    cursor: "grab",
    display: "flex",
    flexDirection: "column",
    "&:active": {
      cursor: "grabbing",
    },
    transition: "box-shadow 0.2s",
    "&:hover": {
      boxShadow: theme.shadows[4],
    },
  },
  cardDragging: {
    opacity: 0.5,
  },
  cardDropWrapper: {
    position: "relative",
  },
  dropLine: {
    height: 3,
    margin: `${theme.spacing(0.25)}px 0`,
    borderRadius: 2,
    backgroundColor: theme.palette.primary.main,
  },
  title: {
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.25,
    marginBottom: 2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    flexShrink: 0,
  },
  // Título, linha de meta e descrição: não estica, para a medição da altura
  // natural do card (useKanbanCardHeight) não ver o espaço que sobra.
  main: {
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
  },
  description: {
    fontSize: 11,
    lineHeight: 1.3,
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    flexShrink: 0,
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  priorityChip: {
    height: 20,
    fontSize: 10,
  },
  actions: {
    padding: theme.spacing(0, 0.5, 0.25),
    gap: 2,
    justifyContent: "flex-end",
    marginTop: "auto",
  },
  // 26px de área de clique: o mínimo confortável sem esticar o card.
  iconButton: {
    padding: 3,
  },
  // Cliente, datas, Vinculado e valor na mesma linha. Quebra em no máximo 2
  // linhas de altura igual; o que passar disso é cortado, para a altura do
  // card não mudar.
  metaInfo: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "2px 8px",
    marginBottom: 2,
    maxHeight: META_LINE_HEIGHT * 2 + 2,
    overflow: "hidden",
    flexShrink: 0,
    "& > *": {
      height: META_LINE_HEIGHT,
      boxSizing: "border-box",
      flexShrink: 0,
    },
  },
  metaItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  inactivityBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    borderRadius: 12,
    padding: "0 6px",
    fontSize: 10,
    fontWeight: 600,
  },
  // Menor e mais baixo que o padrão do MUI (20px, metade para fora do ícone):
  // o padrão subia até encostar na barra de tarefas.
  tasksBadge: {
    height: 16,
    minWidth: 16,
    padding: "0 4px",
    fontSize: 10,
    top: 4,
  },
  overdueBadge: {
    backgroundColor: "#f44336",
    color: "#ffffff",
  },
  noOverdueBadge: {
    backgroundColor: isDarkTheme(theme) ? theme.palette.background.paper : "#ffffff",
    color: isDarkTheme(theme) ? blue[300] : "#2196f3",
    border: `1px solid ${isDarkTheme(theme) ? blue[300] : "#2196f3"}`,
  },
  linkedBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    borderRadius: 12,
    padding: "0 6px",
    fontSize: 10,
    fontWeight: 600,
    backgroundColor: isDarkTheme(theme) ? alpha(blue[500], 0.2) : "#e3f2fd",
    color: isDarkTheme(theme) ? blue[300] : "#1565c0",
    border: `1px solid ${isDarkTheme(theme) ? alpha(blue[300], 0.5) : "#90caf9"}`,
  },
  valueBadge: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: 12,
    fontWeight: 600,
    color: isDarkTheme(theme) ? green[300] : "#2e7d32",
    padding: "0 6px",
    backgroundColor: isDarkTheme(theme) ? alpha(green[500], 0.2) : "#e8f5e9",
    borderRadius: 4,
    whiteSpace: "nowrap",
  },
  // Tarefas ficam presas embaixo, logo acima dos ícones, com o mesmo respiro
  // dos dois lados; o espaço que sobra em card mais vazio fica acima delas.
  tasks: {
    marginTop: "auto",
    paddingTop: theme.spacing(0.75),
    flexShrink: 0,
  },
  tasksProgressRoot: {
    backgroundColor: isDarkTheme(theme) ? theme.palette.action.selected : "#e0e0e0",
  },
  tasksLabel: {
    fontSize: 10.5,
    lineHeight: 1.2,
    color: theme.palette.text.secondary,
  },
  tasksDone: {
    color: isDarkTheme(theme) ? green[300] : "#4caf50",
  },
  tasksProgressBar: {
    backgroundColor: "#2196f3",
  },
  // minHeight 0 + overflow: com altura fixa, conteúdo a mais é cortado aqui
  // e nunca empurra os ícones para fora do card.
  content: {
    flexGrow: 1,
    minHeight: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(0.75, 1.25),
  },
}));

const priorityColors = {
  low: "#3498db",
  medium: "#f39c12",
  high: "#e67e22",
  urgent: "#e74c3c",
};

const priorityLabels = {
  low: "Baixa",
  medium: "Média",
  high: "Alta",
  urgent: "Urgente",
};

const KanbanCard = ({
  card,
  index,
  onEdit,
  onDelete,
  onClone,
  onDragStart,
  onDragEnd,
  isDragging,
  canEdit = false,
  canDelete = false,
  canClone = false,
  onTasksUpdate, // Novo callback para atualizar quando tarefas mudarem
  inactivityTimerConfig,
  manualSortEnabled = false,
  onCardDragOver,
  onCardDrop,
  dropIndicator = null, // null | "top" | "bottom"
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const [tasksModalOpen, setTasksModalOpen] = useState(false);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [ticketUuid, setTicketUuid] = useState(null);
  const [loadingTicket, setLoadingTicket] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [newTicketContact, setNewTicketContact] = useState(null);
  // Ticket vinculado ao card nesta sessão. O card só é recarregado quando o
  // quadro recarrega; sem isso um segundo clique não via o vínculo recém-feito,
  // tentava criar outro chamado e levava 409 (ERR_OTHER_OPEN_TICKET).
  const [linkedTicketId, setLinkedTicketId] = useState(null);
  const { user } = useContext(AuthContext);
  const { getSetting } = useContext(SettingsContext);

  // Extrair dados do card (customFields ou campos diretos)
  // IMPORTANTE: Priorizar campos diretos (como priority) que são sincronizados automaticamente
  const cardData = {
    title: card.customFields?.title || card.title || "Card sem título",
    description: card.customFields?.description || card.description || "",
    priority: card.priority || card.customFields?.priority || null, // Priorizar campo direto
    dueDate: card.dueDate || card.customFields?.dueDate || null, // Priorizar campo direto
    assignedUser: card.assignedUser || null,
    cliente: card.cliente || null, // Cliente vinculado
    headerColor: card.customFields?.headerColor || null, // Cor personalizada do header
    valorReais: card.customFields?.valorReais || null, // Valor R$
    tasks: card.tasks || [], // Tarefas do card
  };

  // Alteracao: GitHub Copilot - 20/04/2026 - contabiliza tarefas vencidas para exibir badge vermelho no icone.
  // Calcular estatísticas de tarefas
  const normalizeDateOnly = (dateValue) => {
    if (!dateValue) return null;

    if (typeof dateValue === "string") {
      const dateOnlyMatch = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (dateOnlyMatch) {
        const year = Number(dateOnlyMatch[1]);
        const month = Number(dateOnlyMatch[2]) - 1;
        const day = Number(dateOnlyMatch[3]);
        const localDate = new Date(year, month, day);

        if (Number.isNaN(localDate.getTime())) return null;
        localDate.setHours(0, 0, 0, 0);
        return localDate;
      }
    }

    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) return null;

    parsedDate.setHours(0, 0, 0, 0);
    return parsedDate;
  };

  const isTaskOverdue = (task) => {
    if (task.completed || !task.dueDate) return false;

    const dueDate = normalizeDateOnly(task.dueDate);
    if (!dueDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dueDate < today;
  };

  const tasksStats = {
    total: cardData.tasks.length,
    completed: cardData.tasks.filter(t => t.completed).length,
    pending: cardData.tasks.filter(t => !t.completed).length,
    overdue: cardData.tasks.filter(t => isTaskOverdue(t)).length,
  };

  const contentRef = useRef(null);
  const actionsRef = useRef(null);
  const boardCardHeight = useKanbanCardHeight(
    { contentRef, actionsRef },
    tasksStats.total > 0
  );

  // Função para formatar data com segurança
  const formatDate = (dateValue) => {
    if (!dateValue) return null;
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return null;
      return format(date, "dd/MM/yyyy");
    } catch (error) {
      console.error("Erro ao formatar data:", error);
      return null;
    }
  };

  // Função para formatar data e hora de criação
  const formatDateTime = (dateValue) => {
    if (!dateValue) return null;
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return null;
      return format(date, "dd/MM/yyyy HH:mm");
    } catch (error) {
      console.error("Erro ao formatar data e hora:", error);
      return null;
    }
  };

  // Função para formatar valor monetário
  const formatCurrency = (value) => {
    if (!value) return null;
    
    // Se já está formatado (contém "R$"), retornar como está
    if (typeof value === 'string' && value.includes('R$')) {
      return value;
    }
    
    // Converter para número se for string
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d,.-]/g, '').replace(',', '.')) : value;
    
    if (isNaN(numValue)) return null;
    
    // Formatar como moeda brasileira
    return numValue.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handleDragStart = (e) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("cardId", card.id);
    e.dataTransfer.setData("columnId", card.columnId);
    if (onDragStart) {
      onDragStart(card);
    }
  };

  const handleDragEnd = (e) => {
    if (onDragEnd) {
      onDragEnd();
    }
  };

  // Detecta a metade (topo/baixo) onde o card arrastado está pairando.
  const resolveDropPosition = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return e.clientY < rect.top + rect.height / 2 ? "top" : "bottom";
  };

  const handleCardDragOver = (e) => {
    if (!manualSortEnabled) return;
    e.preventDefault();
    e.stopPropagation(); // impede o onDragOver da coluna de assumir
    onCardDragOver?.(index, resolveDropPosition(e), e);
  };

  const handleCardDrop = (e) => {
    if (!manualSortEnabled) return;
    e.preventDefault();
    e.stopPropagation(); // impede o handleDrop da coluna (que joga pro fim)
    onCardDrop?.(index, resolveDropPosition(e), e);
  };

  const effectiveTicketId = card.ticketId || linkedTicketId;

  const openTicketChat = (uuid) => {
    setTicketUuid(uuid);
    setTicketModalOpen(true);
  };

  // Vincula o chamado ao card (no backend e localmente, para os próximos cliques)
  const linkTicketToCard = async (ticketId) => {
    if (!ticketId) return;
    setLinkedTicketId(ticketId);
    try {
      await api.put(`/kanban/cards/${card.id}`, { ticketId });
    } catch (err) {
      console.error("Erro ao vincular ticket ao card:", err);
    }
  };

  // Função para abrir chat do WhatsApp
  const handleOpenChat = async () => {
    if (!card.contactId && !effectiveTicketId) {
      toast.error("Este card não possui um contato vinculado");
      return;
    }

    setLoadingTicket(true);

    try {
      // Se o card já tem um ticket vinculado, busca o ticket para pegar o UUID
      if (effectiveTicketId) {
        const { data: ticketData } = await api.get(`/tickets/${effectiveTicketId}`);
        openTicketChat(ticketData.uuid);
        return;
      }

      // Chamado aberto/aguardando do contato, com qualquer atendente.
      // queueIds é obrigatório: sem ele o backend só devolve chamados SEM fila
      // e um chamado já existente passava despercebido (criava outro → 409).
      const userQueueIds = (user?.queues || []).map((q) => q.id);
      const { data } = await api.get(`/tickets`, {
        params: {
          contactId: card.contactId,
          status: ["open", "pending"],
          showAll: "true",
          pageNumber: 1,
          queueIds: JSON.stringify(userQueueIds),
        },
      });

      // Um por conexão: o chamado de outra conexão é a conversa de outro número.
      const candidates = isPerConnectionContactTickets(getSetting)
        ? ticketsOnOwnConnections(data.tickets, user)
        : data.tickets || [];
      const existingTicket = candidates[0];
      if (existingTicket) {
        await linkTicketToCard(existingTicket.id);
        openTicketChat(existingTicket.uuid);
        return;
      }

      // Sem chamado: o atendente escolhe fila e conexão e assume o chamado no
      // modal "Novo ticket". O POST cru (só contactId) criava o chamado SEM
      // atendente, na conexão padrão e na única fila dela (ex.: fila de IA), e
      // ele aparecia "em atendimento" para quem usa "Ver todos".
      const { data: contact } = await api.get(`/contacts/${card.contactId}`);
      setNewTicketContact(contact);
      setNewTicketModalOpen(true);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingTicket(false);
    }
  };

  // Fechamento do "Novo ticket": com chamado (criado ou o existente que o
  // usuário escolheu abrir), vincula ao card e abre o chat; sem, só fecha.
  const handleNewTicketModalClose = async (ticket) => {
    setNewTicketModalOpen(false);
    setNewTicketContact(null);
    if (!ticket || !ticket.uuid) return;
    await linkTicketToCard(ticket.id);
    openTicketChat(ticket.uuid);
  };

  const formattedDueDate = formatDate(cardData.dueDate);
  const formattedValorReais = formatCurrency(cardData.valorReais);
  const formattedCreatedAt = formatDateTime(card.createdAt);
  const hasLinkedNegotiation = Boolean(card.negotiationRef);

  const parseDays = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
  };

  const inactivityConfig = {
    enabled: Boolean(inactivityTimerConfig?.enabled),
    warningDays: parseDays(inactivityTimerConfig?.warningDays, 1),
    dangerDays: parseDays(inactivityTimerConfig?.dangerDays, 3),
    criticalDays: parseDays(inactivityTimerConfig?.criticalDays, 5),
    warningColor: inactivityTimerConfig?.warningColor || "#ffeb3b",
    dangerColor: inactivityTimerConfig?.dangerColor || "#f44336",
    criticalColor: inactivityTimerConfig?.criticalColor || "#000000",
  };

  const inactivityReference = card.updatedAt || card.createdAt;
  const inactivityDate = inactivityReference ? new Date(inactivityReference) : null;
  const inactivityDays =
    inactivityDate && !Number.isNaN(inactivityDate.getTime())
      ? Math.floor((Date.now() - inactivityDate.getTime()) / (1000 * 60 * 60 * 24))
      : 0;

  let inactivityLevel = "none";
  let inactivityColor = null;

  if (!inactivityConfig.enabled) {
    inactivityColor = null;
  } else if (inactivityDays >= inactivityConfig.criticalDays) {
    inactivityLevel = "critical";
    inactivityColor = inactivityConfig.criticalColor;
  } else if (inactivityDays >= inactivityConfig.dangerDays) {
    inactivityLevel = "danger";
    inactivityColor = inactivityConfig.dangerColor;
  } else if (inactivityDays >= inactivityConfig.warningDays) {
    inactivityLevel = "warning";
    inactivityColor = inactivityConfig.warningColor;
  }

  const inactivityBadgeStyle = inactivityColor
    ? {
        backgroundColor: inactivityColor,
        color: inactivityLevel === "warning" ? "#1a1a1a" : "#ffffff",
      }
    : null;

  const hexToRgba = (hex, alpha) => {
    if (!hex || typeof hex !== "string") return `rgba(0,0,0,${alpha})`;
    const cleaned = hex.replace("#", "");
    if (cleaned.length !== 6) return `rgba(0,0,0,${alpha})`;

    const r = parseInt(cleaned.slice(0, 2), 16);
    const g = parseInt(cleaned.slice(2, 4), 16);
    const b = parseInt(cleaned.slice(4, 6), 16);

    if ([r, g, b].some((v) => Number.isNaN(v))) {
      return `rgba(0,0,0,${alpha})`;
    }

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const inactivityCardStyle = inactivityColor
    ? {
        borderLeft: `4px solid ${inactivityColor}`,
        boxShadow: `inset 130px 0 110px -95px ${hexToRgba(inactivityColor, 0.15)}`,
      }
    : undefined;

  return (
    <div
      className={classes.cardDropWrapper}
      onDragOver={handleCardDragOver}
      onDrop={handleCardDrop}
    >
      {dropIndicator === "top" && <div className={classes.dropLine} />}
      <Card
        id={`kanban-card-${card.id}`}
        data-card-id={card.id}
        className={`${classes.card} ${isDragging ? classes.cardDragging : ""}`}
        style={{ ...inactivityCardStyle, minHeight: boardCardHeight || undefined }}
        draggable
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
      <CardContent
        ref={contentRef}
        className={classes.content}
        style={
          cardData.headerColor
            ? {
                // Corpo na cor do Paper: branco fixo deixava o texto do tema escuro ilegível
                background: `linear-gradient(to bottom, ${cardData.headerColor} 0%, ${cardData.headerColor} 24px, ${theme.palette.background.paper} 24px)`,
              }
            : undefined
        }
      >
        <div className={classes.main}>
          <Typography 
            className={classes.title}
            title={cardData.title}
            style={cardData.headerColor ? { color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.3)' } : {}}
          >
            {cardData.title}
          </Typography>

          <div className={classes.metaInfo}>
            {/* {cardData.priority && (
              <Chip
                label={priorityLabels[cardData.priority]}
                size="small"
                className={classes.priorityChip}
                style={{
                  backgroundColor: priorityColors[cardData.priority],
                  color: "#fff",
                }}
              />
            )} */}

            {cardData.cliente && (
              <Tooltip title={`Cliente: ${cardData.cliente.NOMEFANTASIA || cardData.cliente.NOMECLI}`}>
                <div className={classes.metaItem}>
                  <BusinessIcon style={{ fontSize: 14 }} />
                  <span style={{ 
                    maxWidth: '150px', 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap' 
                  }}>
                    {cardData.cliente.NOMEFANTASIA || cardData.cliente.NOMECLI}
                  </span>
                </div>
              </Tooltip>
            )}

            {cardData.assignedUser && (
              <Tooltip title={cardData.assignedUser.name}>
                <div className={classes.metaItem}>
                  <PersonIcon style={{ fontSize: 14 }} />
                  <span style={{
                    maxWidth: '150px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {cardData.assignedUser.name}
                  </span>
                </div>
              </Tooltip>
            )}

            {formattedDueDate && (
              <Tooltip title="Data de vencimento">
                <div className={classes.metaItem}>
                  <EventIcon style={{ fontSize: 14 }} />
                  <span>{formattedDueDate}</span>
                </div>
              </Tooltip>
            )}

            {formattedCreatedAt && (
              <Tooltip title="Data de criação">
                <div className={classes.metaItem}>
                  <EventIcon style={{ fontSize: 14, opacity: 0.7 }} />
                  <span style={{ fontSize: 10, opacity: 0.8 }}>{formattedCreatedAt}</span>
                </div>
              </Tooltip>
            )}

            {hasLinkedNegotiation && (
              <Tooltip title={`Negociação vinculada`}>
                <div className={classes.linkedBadge}>
                  <LinkIcon style={{ fontSize: 12 }} />
                  <span>Vinculado</span>
                </div>
              </Tooltip>
            )}

            {formattedValorReais && (
              <span className={classes.valueBadge}>{formattedValorReais}</span>
            )}

            {inactivityColor && (
              <Tooltip title={`Sem interação há ${inactivityDays} dia(s)`}>
                <div className={classes.inactivityBadge} style={inactivityBadgeStyle}>
                  <ScheduleIcon style={{ fontSize: 12 }} />
                  <span>{`${inactivityDays}d`}</span>
                </div>
              </Tooltip>
            )}
          </div>

          {cardData.description && (
            <Typography className={classes.description} title={cardData.description}>
              {cardData.description}
            </Typography>
          )}
        </div>

        {/* Progresso de Tarefas */}
        {tasksStats.total > 0 && (
          <Box className={classes.tasks}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb="2px">
              <Typography variant="caption" className={classes.tasksLabel}>
                Tarefas
              </Typography>
              <Typography
                variant="caption"
                className={`${classes.tasksLabel} ${tasksStats.completed === tasksStats.total ? classes.tasksDone : ""}`}
                style={{ fontWeight: 600 }}
              >
                {tasksStats.completed}/{tasksStats.total}
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={(tasksStats.completed / tasksStats.total) * 100}
              classes={{
                root: classes.tasksProgressRoot,
                bar: classes.tasksProgressBar,
              }}
              style={{
                height: 3,
                borderRadius: 2,
              }}
            />
          </Box>
        )}
      </CardContent>

      <CardActions ref={actionsRef} className={classes.actions} disableSpacing>
        {/* Botão WhatsApp - apenas se tiver contato ou ticket vinculado */}
        {(card.contactId || effectiveTicketId) && (
          <Tooltip title="Conversar no WhatsApp">
            <IconButton
              className={classes.iconButton}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenChat();
              }}
              disabled={loadingTicket}
            >
              <WhatsAppIcon fontSize="small" style={{ color: "#25d366" }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Tarefas">
          <span>
            <IconButton
              className={classes.iconButton}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (canEdit) {
                  setTasksModalOpen(true);
                }
              }}
              disabled={!canEdit}
            >
              <Badge 
                badgeContent={tasksStats.overdue > 0 ? tasksStats.overdue : tasksStats.total}
                color="default"
                classes={{ badge: `${classes.tasksBadge} ${tasksStats.overdue > 0 ? classes.overdueBadge : classes.noOverdueBadge}` }}
                invisible={tasksStats.total === 0}
              >
                <AssignmentIcon fontSize="small" />
              </Badge>
            </IconButton>
          </span>
        </Tooltip>
        {canClone && (
          <Tooltip title="Enviar para outro kanban">
            <IconButton
              className={classes.iconButton}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onClone?.(card);
              }}
            >
              <SendIcon
                fontSize="small"
                style={{
                  transform: "rotate(-33deg)",
                }}
              />
            </IconButton>
          </Tooltip>
        )}
        {canEdit && (
          <Tooltip title="Editar">
            <IconButton
              className={classes.iconButton}
              size="small"
              onClick={() => onEdit(card)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {canDelete && (
          <Tooltip title="Excluir">
            <IconButton
              className={classes.iconButton}
              size="small"
              onClick={() => onDelete(card)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </CardActions>

      {/* Os modais só montam enquanto abertos: fechados, eles custavam dezenas de
          componentes por card e o board chega a ter mais de mil cards. */}
      {/* Modal de Tarefas */}
      {tasksModalOpen && (
        <TasksModal
          open={tasksModalOpen}
          onClose={() => setTasksModalOpen(false)}
          card={card}
          onSuccess={onTasksUpdate}
        />
      )}

      {/* Modal de Chat do Ticket */}
      {ticketModalOpen && (
        <TicketModal
          open={ticketModalOpen}
          onClose={() => {
            setTicketModalOpen(false);
            setTicketUuid(null);
          }}
          ticketId={ticketUuid}
        />
      )}

      {/* Novo chamado a partir do card: atendente escolhe fila/conexão e assume */}
      {newTicketModalOpen && (
        <NewTicketModal
          modalOpen={newTicketModalOpen}
          initialContact={newTicketContact}
          onClose={handleNewTicketModalClose}
        />
      )}
      </Card>
      {dropIndicator === "bottom" && <div className={classes.dropLine} />}
    </div>
  );
};

// Memo: a página re-renderiza a cada tecla no filtro, arraste ou modal aberto;
// sem ele todos os cards visíveis eram redesenhados junto.
export default React.memo(KanbanCard);
